import {
  useResultTemplatesQuery,
  useGetSchoolGradeGroupsQuery,
  useGetAssessmentTypesQuery,
  useCreateResultTemplateMutation,
  useUpdateResultTemplateMutation,
} from '@/types/vue-apollo-helpers';
import type { ResultTemplateColumn, ResultTemplateFeedback, SchoolAssessmentType, GradeGroup, ResultTemplate } from '@/types/composition-functions';
import { useToast } from 'primevue/usetoast';
import {
  emptyFeedbackForm, feedbackDefinitionPayload, resizeRatingLabels, validateFeedbackForm,
  type FeedbackDraft,
} from '@/utils/resultFeedbackRating';
import { ResultFeedbackInputType } from '@/types/composition-functions';

type ColumnType = 'ASSESSMENT' | 'CALCULATION' | 'HISTORICAL' | 'GRADE' | 'UNIT' | 'GRADE_POINT' | 'WEIGHTED_GRADE_POINT';

export function useResultTemplateBuilder(props: { templateId?: string | null }, emit: (event: 'close' | 'saved') => void, isTertiary: { value: boolean }) {
  const toast = useToast();

  // State
  const form = reactive({
    name: '',
    code: '',
    grade_group_id: '',
    style_code: 'classic',
    chart_column: '',
    chart_type: 'radar',
  });

  const columns = ref<Record<string, unknown>[]>([]);
  const saving = ref(false);
  const showColumnModal = ref(false);
  const activeColumnType = ref<ColumnType>('ASSESSMENT');
  const showCreateAssessmentModal = ref(false);

  // Feedbacks
  const feedbacks = ref<FeedbackDraft[]>([]);
  const showFeedbackModal = ref(false);
  const feedbackForm = reactive(emptyFeedbackForm());
  /** Index of the feedback item being edited, or null when adding (#574 ratings). */
  const editingFeedbackIndex = ref<number | null>(null);
  const feedbackFormError = ref('');

  // Keep one label per level as the admin changes the scale.
  watch(() => feedbackForm.rating_max, (max) => {
    if (Number.isInteger(max) && max >= 2 && max <= 10) {
      feedbackForm.rating_labels = resizeRatingLabels(feedbackForm.rating_labels, max);
    }
  });

  const assignedProgrammes = ref<Record<string, unknown>[]>([]);

  // Summaries
  const summaries = ref<Record<string, unknown>[]>([]);
  const showSummaryModal = ref(false);
  const editingSummaryIndex = ref<number | null>(null);
  const summaryForm = reactive({
    label: '',
    key: '',
    calculation_function: 'PERCENTAGE',
    source_key: '',
    max_score: '100',
    unit_key: 'unit',
    weighted_grade_point_key: 'weighted_grade_point',
  });

  // Column Form
  const columnForm = reactive({
    label: '',
    key: '',
    school_assessment_type_id: '',
    calculation_function: 'SUM',
    calculation_inputs: [] as string[],
    ignore_nulls: false,
    source_key: '',
    grade_source: '',
    grade_output: 'grade',
    unit_key: '',
    grade_point_key: '',
  });

  const editingColumnIndex = ref<number | null>(null);
  const isEditingColumn = computed(() => editingColumnIndex.value !== null);
  const currentAcademicCalendarId = ref('1');

  // Queries
  const { result: gradeGroupsResult } = useGetSchoolGradeGroupsQuery();
  const { result: assessmentTypesResult, refetch: refetchAssessments } = useGetAssessmentTypesQuery();
  const { result: templateResult, onResult } = useResultTemplatesQuery(() => ({
    include_programmes: isTertiary.value,
  }), { enabled: !!props.templateId });

  // Mutations
  const { mutate: createTemplate } = useCreateResultTemplateMutation();
  const { mutate: updateTemplate } = useUpdateResultTemplateMutation();

  // Computed
  const isEditing = computed(() => !!props.templateId);
  const gradeGroups = computed<GradeGroup[]>(() => gradeGroupsResult.value?.getSchoolGradeGroups || []);
  const allAssessments = computed<SchoolAssessmentType[]>(() => assessmentTypesResult.value?.schoolAssessmentTypes || []);
  const availableColumnKeys = computed(() => columns.value.map(c => c.key));

  const genericAssessments = computed<SchoolAssessmentType[]>(() => allAssessments.value.filter((a) => !a.academic_calendar_id));
  const termAssessments = computed<SchoolAssessmentType[]>(() => allAssessments.value.filter((a) => !!a.academic_calendar_id));
  const groupedAssessments = computed(() => [
    { label: 'Generic / Reusable', items: genericAssessments.value },
    { label: 'Current Term', items: termAssessments.value },
  ]);

  const availableColumnsForCalc = computed(() => columns.value);

  // Load existing template data
  onResult((res) => {
    if (res.data?.resultTemplates?.data && props.templateId) {
      const found = res.data.resultTemplates.data.find((t: ResultTemplate) => t.id === props.templateId);
      if (found) {
        form.name = found.name;
        form.code = found.code;
        form.grade_group_id = found.grade_group?.id;
        form.style_code = found.style_code || 'classic';
        form.chart_column = found.properties?.chart?.column || '';
        form.chart_type = found.properties?.chart?.type || 'radar';

        if (found.properties?.summaries) {
          summaries.value = found.properties.summaries;
        }

        columns.value = found.columns.map((c: ResultTemplateColumn) => ({
          ...c,
          tempId: Math.random().toString(36).substr(2, 9),
          school_assessment_type_id: c.school_assessment_type?.id,
        })).sort((a, b) => a.sequence - b.sequence);

        if (found.feedbacks) {
          feedbacks.value = [...found.feedbacks]
            .sort((a: ResultTemplateFeedback, b: ResultTemplateFeedback) => a.sequence - b.sequence)
            .map((f: ResultTemplateFeedback) => ({
              label: f.label, code: f.code, category: f.category, id: f.id,
              input_type: f.input_type, rating_max: f.rating_max, rating_labels: f.rating_labels ?? null,
            }));
        }

        assignedProgrammes.value = found.programmes || [];
      }
    }
  });

  // Column methods
  function openAddColumnModal(type: ColumnType) {
    activeColumnType.value = type;
    editingColumnIndex.value = null;
    resetColumnForm();
    showColumnModal.value = true;
  }

  function resetColumnForm() {
    columnForm.label = '';
    columnForm.key = '';
    columnForm.school_assessment_type_id = '';
    columnForm.calculation_function = 'SUM';
    columnForm.calculation_inputs = [];
    columnForm.ignore_nulls = false;
    columnForm.source_key = '';
    columnForm.grade_source = '';
    columnForm.grade_output = activeColumnType.value === 'GRADE_POINT' ? 'point' : 'grade';
  }

  function editColumn(index: number) {
    const col = columns.value[index];
    editingColumnIndex.value = index;
    activeColumnType.value = col.type as ColumnType;

    columnForm.label = col.label;
    columnForm.key = col.key;

    if (col.type === 'ASSESSMENT') {
      columnForm.school_assessment_type_id = col.school_assessment_type_id;
    } else if (col.type === 'CALCULATION') {
      columnForm.calculation_function = col.calculation_function;
      let parsed = col.calculation_arguments;
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch (e) { parsed = null; }
      }
      if (Array.isArray(parsed)) {
        columnForm.calculation_inputs = parsed;
        columnForm.ignore_nulls = false;
      } else if (typeof parsed === 'object' && parsed !== null) {
        columnForm.calculation_inputs = parsed.keys || [];
        columnForm.ignore_nulls = !!parsed.ignore_nulls;
      } else {
        columnForm.calculation_inputs = [];
        columnForm.ignore_nulls = false;
      }
    } else if (col.type === 'GRADE') {
      const args = col.calculation_arguments;
      if (args && typeof args === 'object') {
        columnForm.grade_source = args.source || '';
        columnForm.grade_output = args.output || 'grade';
      }
    } else if (col.type === 'HISTORICAL') {
      columnForm.source_key = col.calculation_arguments;
    } else if (col.type === 'GRADE_POINT') {
      activeColumnType.value = 'GRADE';
      const args = col.calculation_arguments;
      if (args && typeof args === 'object') {
        columnForm.grade_source = args.source;
        columnForm.grade_output = 'point';
      }
    } else if (col.type === 'WEIGHTED_GRADE_POINT') {
      const args = col.calculation_arguments;
      if (args && typeof args === 'object') {
        columnForm.unit_key = args.unit_key;
        columnForm.grade_point_key = args.grade_point_key;
      }
    }

    showColumnModal.value = true;
  }

  function saveColumn() {
    if (!columnForm.label || !columnForm.key) {
      toast.add({ severity: 'warn', summary: 'Validation', detail: 'Label and Key are required.', life: 3000 });
      return;
    }

    const duplicate = columns.value.find((c, i) => c.key === columnForm.key && i !== editingColumnIndex.value);
    if (duplicate) {
      toast.add({ severity: 'warn', summary: 'Validation', detail: 'Key must be unique.', life: 3000 });
      return;
    }

    const colData: Record<string, unknown> = {
      label: columnForm.label,
      key: columnForm.key,
      type: activeColumnType.value,
      is_visible: true,
    };

    if (isEditingColumn.value && editingColumnIndex.value !== null) {
      const existing = columns.value[editingColumnIndex.value];
      colData.tempId = existing.tempId;
      colData.sequence = existing.sequence;
      colData.is_visible = existing.is_visible;
    } else {
      colData.tempId = Date.now().toString();
      colData.sequence = columns.value.length + 1;
    }

    if (activeColumnType.value === 'ASSESSMENT') {
      if (!columnForm.school_assessment_type_id) { toast.add({ severity: 'warn', summary: 'Validation', detail: 'Please select an assessment.', life: 3000 }); return; }
      const selectedAssessment = allAssessments.value.find((a) => a.id === columnForm.school_assessment_type_id);
      colData.school_assessment_type_id = columnForm.school_assessment_type_id;
      colData.school_assessment_type = selectedAssessment;
    } else if (activeColumnType.value === 'CALCULATION') {
      colData.calculation_function = columnForm.calculation_function;
      colData.calculation_arguments = columnForm.ignore_nulls
        ? JSON.stringify({ keys: columnForm.calculation_inputs, ignore_nulls: true })
        : JSON.stringify(columnForm.calculation_inputs);
    } else if (activeColumnType.value === 'GRADE') {
      if (!columnForm.grade_source) { toast.add({ severity: 'warn', summary: 'Validation', detail: 'Source Column is required for Grade columns.', life: 3000 }); return; }
      if (columnForm.grade_output === 'point') {
        colData.type = 'GRADE_POINT';
        colData.calculation_arguments = { source: columnForm.grade_source };
        if (!columnForm.key) colData.key = 'grade_point';
      } else {
        colData.calculation_arguments = { source: columnForm.grade_source, output: columnForm.grade_output };
      }
    } else if (activeColumnType.value === 'HISTORICAL') {
      if (!columnForm.source_key) { toast.add({ severity: 'warn', summary: 'Validation', detail: 'Source Key is required for Historical columns.', life: 3000 }); return; }
      colData.calculation_arguments = columnForm.source_key;
    } else if (activeColumnType.value === 'UNIT') {
      colData.calculation_arguments = null;
      if (!columnForm.key) colData.key = 'unit';
    } else if (activeColumnType.value === 'GRADE_POINT') {
      colData.calculation_arguments = { source: columnForm.grade_source };
      if (!columnForm.key) colData.key = 'grade_point';
    } else if (activeColumnType.value === 'WEIGHTED_GRADE_POINT') {
      colData.calculation_arguments = { unit_key: columnForm.unit_key, grade_point_key: columnForm.grade_point_key };
      if (!columnForm.key) colData.key = 'weighted_grade_point';
    }

    if (isEditingColumn.value && editingColumnIndex.value !== null) {
      columns.value[editingColumnIndex.value] = colData;
    } else {
      columns.value.push(colData);
    }

    showColumnModal.value = false;
    editingColumnIndex.value = null;
  }

  // Display helpers
  function getTypeBadgeClass(type: string) {
    const map: Record<string, string> = {
      ASSESSMENT: 'bg-blue-100 text-blue-800',
      CALCULATION: 'bg-primary/10 text-primary',
      HISTORICAL: 'bg-purple-100 text-purple-800',
      GRADE: 'bg-orange-100 text-orange-800',
      UNIT: 'bg-teal-100 text-teal-800',
      GRADE_POINT: 'bg-teal-100 text-teal-800',
      WEIGHTED_GRADE_POINT: 'bg-teal-100 text-teal-800',
    };
    return map[type] || 'bg-gray-100 text-gray-800';
  }

  function hasSmartCalc(args: unknown) {
    if (!args) return false;
    if (typeof args === 'object' && !Array.isArray(args)) return !!(args as Record<string, unknown>).ignore_nulls;
    if (typeof args === 'string') {
      try {
        const parsed = JSON.parse(args);
        return typeof parsed === 'object' && !Array.isArray(parsed) && !!parsed.ignore_nulls;
      } catch { return false; }
    }
    return false;
  }

  function getCalculationArgsDisplay(args: unknown): string {
    if (typeof args === 'string') {
      try {
        const parsed = JSON.parse(args);
        if (Array.isArray(parsed)) return parsed.join(', ');
        if (typeof parsed === 'object' && parsed !== null) return parsed.keys ? parsed.keys.join(', ') : '';
      } catch { return args; }
      return args;
    }
    if (Array.isArray(args)) return args.join(', ');
    if (typeof args === 'object' && args !== null) {
      const obj = args as Record<string, unknown>;
      if (obj.source) return `Source: ${obj.source}, Output: ${obj.output}`;
      if (obj.keys) return (obj.keys as string[]).join(', ');
    }
    return String(args ?? '');
  }

  function getCategoryBadgeClass(category: string) {
    const map: Record<string, string> = {
      affective: 'bg-purple-100 text-purple-700',
      psychomotor: 'bg-blue-100 text-blue-700',
      general: 'bg-gray-100 text-gray-700',
    };
    return map[category] || 'bg-gray-100 text-gray-600';
  }

  function getCategoryLabel(category: string) {
    const map: Record<string, string> = { affective: 'Affective', psychomotor: 'Psychomotor', general: 'General' };
    return map[category] || category;
  }

  function toggleVisibility(index: number) {
    columns.value[index].is_visible = !columns.value[index].is_visible;
  }

  function handleAssessmentCreated(newAssessment: SchoolAssessmentType) {
    refetchAssessments();
    columnForm.school_assessment_type_id = newAssessment.id;
  }

  // Feedback methods
  function openAddFeedbackModal() {
    Object.assign(feedbackForm, emptyFeedbackForm());
    editingFeedbackIndex.value = null;
    feedbackFormError.value = '';
    showFeedbackModal.value = true;
  }

  function openEditFeedbackModal(index: number) {
    const draft = feedbacks.value[index];
    if (!draft) return;
    const base = emptyFeedbackForm();
    const max = draft.rating_max ?? base.rating_max;
    Object.assign(feedbackForm, {
      label: draft.label,
      code: draft.code ?? '',
      category: draft.category ?? '',
      input_type: draft.input_type ?? ResultFeedbackInputType.Text,
      rating_max: max,
      rating_labels: draft.rating_labels?.length ? [...draft.rating_labels] : resizeRatingLabels(base.rating_labels, max),
    });
    editingFeedbackIndex.value = index;
    feedbackFormError.value = '';
    showFeedbackModal.value = true;
  }

  function saveFeedback() {
    const problem = validateFeedbackForm(feedbackForm);
    if (problem) {
      feedbackFormError.value = problem;
      return;
    }
    const isRating = feedbackForm.input_type === ResultFeedbackInputType.Rating;
    const draft: FeedbackDraft = {
      label: feedbackForm.label.trim(),
      code: feedbackForm.code || feedbackForm.label.toUpperCase().replace(/\s+/g, '_'),
      category: feedbackForm.category || null,
      input_type: feedbackForm.input_type,
      rating_max: isRating ? feedbackForm.rating_max : null,
      rating_labels: isRating ? [...feedbackForm.rating_labels] : null,
    };
    if (editingFeedbackIndex.value !== null) {
      const existing = feedbacks.value[editingFeedbackIndex.value];
      feedbacks.value[editingFeedbackIndex.value] = { ...draft, id: existing?.id };
    } else {
      feedbacks.value.push(draft);
    }
    feedbackFormError.value = '';
    showFeedbackModal.value = false;
  }

  function removeFeedback(index: number) {
    feedbacks.value.splice(index, 1);
  }

  function moveFeedback(index: number, direction: number) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= feedbacks.value.length) return;
    const [moved] = feedbacks.value.splice(index, 1);
    if (moved) feedbacks.value.splice(newIndex, 0, moved);
  }

  // Summary methods
  function openAddSummaryModal() {
    editingSummaryIndex.value = null;
    summaryForm.label = '';
    summaryForm.key = '';
    summaryForm.calculation_function = 'PERCENTAGE';
    summaryForm.source_key = '';
    summaryForm.max_score = '100';
    summaryForm.unit_key = 'unit';
    summaryForm.weighted_grade_point_key = 'weighted_grade_point';
    showSummaryModal.value = true;
  }

  function editSummary(index: number) {
    const s = summaries.value[index];
    editingSummaryIndex.value = index;
    summaryForm.label = s.label;
    summaryForm.key = s.key;
    summaryForm.calculation_function = s.calculation_function;
    summaryForm.source_key = s.source_key;
    summaryForm.max_score = s.max_score?.toString() || '100';
    if (['GPA', 'CGPA'].includes(s.calculation_function)) {
      summaryForm.unit_key = s.unit_key || 'unit';
      summaryForm.weighted_grade_point_key = s.weighted_grade_point_key || 'weighted_grade_point';
    }
    showSummaryModal.value = true;
  }

  function saveSummary() {
    if (!summaryForm.label || !summaryForm.key) { toast.add({ severity: 'warn', summary: 'Validation', detail: 'Label and Key are required', life: 3000 }); return; }
    const summaryData: Record<string, unknown> = {
      label: summaryForm.label,
      key: summaryForm.key,
      calculation_function: summaryForm.calculation_function,
      source_key: summaryForm.source_key,
      max_score: summaryForm.calculation_function === 'PERCENTAGE' ? (summaryForm.max_score || 100) : undefined,
    };
    if (['GPA', 'CGPA'].includes(summaryForm.calculation_function)) {
      summaryData.unit_key = summaryForm.unit_key;
      summaryData.weighted_grade_point_key = summaryForm.weighted_grade_point_key;
    }
    if (editingSummaryIndex.value !== null) {
      summaries.value[editingSummaryIndex.value] = summaryData;
    } else {
      summaries.value.push(summaryData);
    }
    showSummaryModal.value = false;
  }

  function removeSummary(index: number) {
    summaries.value.splice(index, 1);
  }

  // Save template
  async function saveTemplate() {
    saving.value = true;
    try {
      const properties: Record<string, unknown> = {};
      if (summaries.value.length > 0) properties.summaries = summaries.value;
      if (form.style_code === 'detailed') {
        properties.chart = { type: form.chart_type, column: form.chart_column };
      }
      const finalProperties = Object.keys(properties).length > 0 ? properties : null;

      const input = {
        name: form.name,
        code: form.code,
        style_code: form.style_code,
        grade_group_id: form.grade_group_id,
        properties: finalProperties,
        columns: columns.value.map((c, i: number) => ({
          label: c.label,
          key: c.key,
          type: c.type,
          sequence: i,
          is_visible: c.is_visible,
          school_assessment_type_id: c.school_assessment_type_id ? parseInt(c.school_assessment_type_id) : null,
          calculation_function: c.calculation_function,
          calculation_arguments: c.calculation_arguments,
        })),
        feedbacks: feedbacks.value.map((f, i: number) => feedbackDefinitionPayload(f, i)),
      };

      if (isEditing.value) {
        await updateTemplate({ input: { ...input, id: props.templateId } });
      } else {
        await createTemplate({ input });
      }

      toast.add({ severity: 'success', summary: 'Success', detail: `Template ${isEditing.value ? 'updated' : 'created'} successfully`, life: 3000 });
      emit('saved');
      emit('close');
    } catch (e) {
      const error = e as Error;
      toast.add({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to save template', life: 3000 });
    } finally {
      saving.value = false;
    }
  }

  return {
    // State
    form,
    columns,
    saving,
    showColumnModal,
    activeColumnType,
    showCreateAssessmentModal,
    feedbacks,
    showFeedbackModal,
    feedbackForm,
    editingFeedbackIndex,
    feedbackFormError,
    assignedProgrammes,
    summaries,
    showSummaryModal,
    editingSummaryIndex,
    summaryForm,
    columnForm,
    editingColumnIndex,
    isEditingColumn,
    currentAcademicCalendarId,

    // Computed
    isEditing,
    gradeGroups,
    allAssessments,
    availableColumnKeys,
    genericAssessments,
    termAssessments,
    groupedAssessments,
    availableColumnsForCalc,

    // Constants
    chartTypeOptions: [
      { label: 'Radar', value: 'radar' },
      { label: 'Bar', value: 'bar' },
      { label: 'Line', value: 'line' },
    ],
    categoryOptions: [
      { label: 'Affective Domain', value: 'affective' },
      { label: 'Psychomotor Domain', value: 'psychomotor' },
      { label: 'General Remarks', value: 'general' },
    ],
    calculationFunctions: [
      { label: 'SUM (Total)', value: 'SUM' },
      { label: 'AVERAGE', value: 'AVERAGE' },
      { label: 'MULTIPLY (Product)', value: 'MULTIPLY' },
      { label: 'PERCENTAGE', value: 'PERCENTAGE' },
    ],
    templateStyles: [
      { label: 'Classic (Default)', value: 'classic' },
      { label: 'Modern', value: 'modern' },
      { label: 'Detailed (with Chart)', value: 'detailed' },
    ],

    // Methods
    openAddColumnModal,
    resetColumnForm,
    editColumn,
    saveColumn,
    getTypeBadgeClass,
    hasSmartCalc,
    getCalculationArgsDisplay,
    getCategoryBadgeClass,
    getCategoryLabel,
    toggleVisibility,
    handleAssessmentCreated,
    openAddFeedbackModal,
    openEditFeedbackModal,
    saveFeedback,
    removeFeedback,
    moveFeedback,
    openAddSummaryModal,
    editSummary,
    saveSummary,
    removeSummary,
    saveTemplate,
  };
}
